import { useCallback } from 'react';
import type { NodeDefinitionDto } from '@api/nodeDefinitions';
import type { ArtifactKind } from '@api/processes';
import type { ProcessNode as ProcessNodeRfType, Port } from '../graphTypes';
import { makeId } from '../graphTypes';
import { resolveOutputKindOptions } from '../taskSkillSelection';

/** Defensive fallbacks when the catalog hasn't grown a group declaration
 *  yet (legacy fixtures, palette nodes that aren't Task-shaped). The
 *  production Task catalog ships {@code outputGroups: [{ min: 1, max: 5,
 *  type: MODEL_3D, default: 1 }]} so these constants only kick in for
 *  test stubs that don't model the variable group. */
const FALLBACK_OUTPUT_MIN = 1;
const FALLBACK_OUTPUT_MAX = 5;
const DEFAULT_OUTPUT_FALLBACK_KIND: ArtifactKind = 'MODEL_3D';

function reindexOutputPortPositions(ports: Port[]): Port[] {
  let o = 0;
  return ports.map((p) =>
    p.direction === 'output' ? { ...p, position: o++ } : p,
  );
}

function catalogDefaultOutputKind(
  definition: NodeDefinitionDto | undefined,
): ArtifactKind {
  if (!definition) return DEFAULT_OUTPUT_FALLBACK_KIND;
  const variableGroup = definition.defaultPorts.outputGroups[0];
  if (variableGroup) return variableGroup.type;
  const firstOutput = definition.defaultPorts.outputs[0];
  if (firstOutput) return firstOutput.portType;
  return DEFAULT_OUTPUT_FALLBACK_KIND;
}

/**
 * Artifact kind for a freshly-added output port. The catalog group default
 * (MODEL_3D) is wrong for skills that don't support it (e.g. CAM), so we snap
 * to what the selection may actually output.
 */
function pickAddedOutputKind(
  definition: NodeDefinitionDto | undefined,
  skillIds: unknown,
): ArtifactKind {
  const fallback = catalogDefaultOutputKind(definition);
  const allowed = resolveOutputKindOptions(definition, skillIds).map(
    (o) => o.value as ArtifactKind,
  );
  if (allowed.length === 0 || allowed.includes(fallback)) return fallback;
  return allowed[0];
}

/** Read the variable-output bounds from the catalog so the editor and
 *  the add/remove handlers share one source of truth. Falls back to
 *  {@link FALLBACK_OUTPUT_MIN} / {@link FALLBACK_OUTPUT_MAX} when the
 *  definition declares no group (statics-only nodes, test stubs). */
export function maxOutputsFor(definition: NodeDefinitionDto | undefined): number {
  return (
    definition?.defaultPorts.outputGroups[0]?.maxCount ?? FALLBACK_OUTPUT_MAX
  );
}

export function minOutputsFor(definition: NodeDefinitionDto | undefined): number {
  return (
    definition?.defaultPorts.outputGroups[0]?.minCount ?? FALLBACK_OUTPUT_MIN
  );
}

export interface ProcessNodeActionHandlersInput {
  readonly id: string;
  readonly published: boolean;
  readonly ports: readonly Port[] | undefined;
  readonly dataBag: Record<string, unknown>;
  readonly updateNodeData: (
    nodeId: string,
    patch: Partial<ProcessNodeRfType['data']>,
  ) => void;
  /**
   * Catalog definition for this node (already resolved by
   * {@code useProcessNodeDerivedState}). Drives the default kind for
   * {@code onAddOutput} (catalog's first variable-output group). Not
   * involved in {@link onFieldChange} any more — bound port types are
   * projected by the BACKEND on every graph save, and the FE just
   * renders the resulting graph from the round-trip response.
   */
  readonly definition?: NodeDefinitionDto;
}

export function useProcessNodeActionHandlers({
  id,
  published,
  ports,
  dataBag,
  updateNodeData,
  definition,
}: ProcessNodeActionHandlersInput) {
  const onFieldChange = useCallback(
    (key: string, next: unknown) => {
      if (published) return;
      // Field edits patch ONLY the changed config key. Bound port types
      // (Input/Output dataType) are projected onto
      // {@code data.ports[*].portType} by the backend on save and
      // round-tripped back through the detail response — the FE never
      // touches port types in response to a config edit, so it's
      // structurally impossible for the FE to leave config and ports
      // out of sync.
      updateNodeData(id, { [key]: next } as Partial<ProcessNodeRfType['data']>);
    },
    [id, published, updateNodeData],
  );

  const onRename = useCallback(
    (newName: string) => {
      if (published) return;
      // Persist directly onto {@code data.label} (= {@code process_nodes.label}
      // on the wire). The 3-dots quick menu is the canonical UI for
      // renaming a node — there is no separate "Label" form input — so the
      // edit must round-trip through the auto-save body, not sit in
      // ephemeral client state. Empty strings clear back to the catalog
      // title via {@code title = label || catalogTitle} downstream.
      const trimmed = newName.trim();
      updateNodeData(id, {
        label: trimmed,
      } as Partial<ProcessNodeRfType['data']>);
    },
    [id, published, updateNodeData],
  );

  const onAddOutput = useCallback(() => {
    if (published || !Array.isArray(ports)) return;
    const currentOutputs = (ports as Port[]).filter((p) => p.direction === 'output');
    if (currentOutputs.length >= maxOutputsFor(definition)) return;
    const kind = pickAddedOutputKind(definition, dataBag.skillIds);
    const newPort: Port = {
      portId: makeId(),
      direction: 'output',
      label: `output_${currentOutputs.length + 1}`,
      portType: kind,
      acceptedKinds: [kind],
      position: currentOutputs.length,
    };
    updateNodeData(id, {
      ports: reindexOutputPortPositions([...(ports as Port[]), newPort]),
    } as Partial<ProcessNodeRfType['data']>);
  }, [id, ports, published, updateNodeData, definition, dataBag.skillIds]);

  const onRemoveOutput = useCallback(
    (portId: string) => {
      if (published || !Array.isArray(ports)) return;
      const remaining = reindexOutputPortPositions(
        (ports as Port[]).filter((p) => p.portId !== portId),
      );
      const patch: Record<string, unknown> = { ports: remaining };
      const oi = dataBag.outputInstructions as Record<string, string> | undefined;
      if (oi && portId in oi) {
        const rest = Object.fromEntries(
          Object.entries(oi).filter(([k]) => k !== portId),
        );
        patch.outputInstructions = Object.keys(rest).length > 0 ? rest : undefined;
      }
      updateNodeData(id, patch as Partial<ProcessNodeRfType['data']>);
    },
    [id, ports, dataBag.outputInstructions, published, updateNodeData],
  );

  const onRenameOutput = useCallback(
    (portId: string, label: string) => {
      if (published || !Array.isArray(ports)) return;
      updateNodeData(id, {
        ports: (ports as Port[]).map((p) =>
          p.portId === portId ? { ...p, label } : p,
        ),
      } as Partial<ProcessNodeRfType['data']>);
    },
    [id, ports, published, updateNodeData],
  );

  /**
   * Change the artifact kind of a single output port. Used by the
   * per-port kind picker in {@code OutputRow} for Task nodes that
   * declare a variable output group. The new kind becomes both the
   * port's primary {@code portType} and the singleton
   * {@code acceptedKinds} — variable-group ports don't have a
   * multi-type catalog set to preserve, so the FE owns the kind
   * verbatim and {@link PortBindingProjection} (backend) passes it
   * through on save.
   *
   * <p>Intentionally permissive on already-wired ports: matches the
   * existing Input/Output {@code dataType} behaviour where the
   * validator surfaces resulting edge mismatches as
   * {@code PORT_TYPE_MISMATCH}. Locking the picker here would create
   * an inconsistency vs the Data-type select on Input/Output.</p>
   */
  const onUpdateOutputPortType = useCallback(
    (portId: string, kind: ArtifactKind) => {
      if (published || !Array.isArray(ports)) return;
      updateNodeData(id, {
        ports: (ports as Port[]).map((p) =>
          p.portId === portId
            ? { ...p, portType: kind, acceptedKinds: [kind] }
            : p,
        ),
      } as Partial<ProcessNodeRfType['data']>);
    },
    [id, ports, published, updateNodeData],
  );

  const onUpdateOutputInstruction = useCallback(
    (portId: string, instruction: string) => {
      if (published) return;
      const current = (dataBag.outputInstructions as Record<string, string>) ?? {};
      const trimmed = instruction.trim();
      const next = trimmed
        ? { ...current, [portId]: trimmed }
        : Object.fromEntries(
            Object.entries(current).filter(([k]) => k !== portId),
          );
      updateNodeData(id, {
        outputInstructions: Object.keys(next).length > 0 ? next : undefined,
      } as Partial<ProcessNodeRfType['data']>);
    },
    [id, dataBag.outputInstructions, published, updateNodeData],
  );

  return {
    onFieldChange,
    onRename,
    onAddOutput,
    onRemoveOutput,
    onRenameOutput,
    onUpdateOutputInstruction,
    onUpdateOutputPortType,
  };
}
